import { Dialog } from 'primereact/dialog';
import { useState, useEffect, useMemo } from 'react';
import { useGetPlatformsQuery, useGetAccountsByPlatformQuery, useGetCampaignsByAccountQuery } from 'src/redux/admin/reporting/reporting.endpoints';
import { Skeleton } from 'src/components/ui/skeleton';
import { useGetClientAccessQuery, useUpdateClientMutation } from 'src/redux/organization/organization.endpoints';
import { ClientView } from 'src/redux/organization/organization.types';
import { Client, ClientAccessRequest } from 'src/pages/client-management/types/client.types';
import { Button } from 'src/components/ui/button';
import { Search } from "lucide-react";
import { Input } from "src/components/ui/input";
import { useUpdateClientAccountAccessMutation } from 'src/pages/client-management/endpoints/client.endpoints';
import { useGetAccountsQuery, useGetCampaignsQuery } from 'src/pages/client-management/endpoints/accounts.endpoints';

interface AssignCampaignsModalProps {
  isOpen: boolean;
  onClose: () => void;
  client: Client;
  onContinue?: () => void;
}

interface SearchBarProps {
  value: string;
  onChange: (value: string) => void;
}

const SearchBar = ({ value, onChange }: SearchBarProps) => (
  <div className="px-4 my-3">
    <div className="relative flex-1 w-full">
      <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 text-gray-400 h-3.5 w-3.5" />
      <Input
        type="text"
        placeholder="Search"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="pl-8 bg-white rounded-lg placeholder:text-gray-400 h-8 w-full text-sm border border-gray-200"
      />
    </div>
  </div>
);

export const AssignCampaignsModal = ({ isOpen, onClose, client, onContinue }: AssignCampaignsModalProps) => {
  const [currentPlatform, setCurrentPlatform] = useState<string>('');
  const [currentAccount, setCurrentAccount] = useState<string>('');
  const [selectedCampaigns, setSelectedCampaigns] = useState<Record<string, string[]>>({});
  const [localClient, setLocalClient] = useState<Client>(client);
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'saved'>('idle');
  const [platformSearch, setPlatformSearch] = useState("");
  const [accountSearch, setAccountSearch] = useState("");
  const [campaignSearch, setCampaignSearch] = useState("");

  const customSort = (a: string, b: string): number => {
    const aStartsWithNumber = /^\d/.test(a);
    const bStartsWithNumber = /^\d/.test(b);
    if (aStartsWithNumber && !bStartsWithNumber) return 1;
    if (!aStartsWithNumber && bStartsWithNumber) return -1;
    return a.localeCompare(b);
  };

  const { data: platforms = [], isFetching: isFetchingPlatforms } = useGetPlatformsQuery({});
  const { data: accounts = [], isFetching: isFetchingAccounts } = useGetAccountsByPlatformQuery({ source: currentPlatform }, { skip: !currentPlatform });
  const { data: campaigns = [], isFetching: isFetchingCampaigns } = useGetCampaignsByAccountQuery({ source: currentPlatform, accountId: [Number(currentAccount)] }, { skip: !currentAccount });
  const { data: clientAccess = [], isFetching: isFetchingAccess } = useGetClientAccessQuery(localClient.ext_client_id);
  const { data: allAccounts = [] } = useGetAccountsQuery({});
  const { data: allCampaigns = [] } = useGetCampaignsQuery({});
  const [updateClientAccountAccess, { isLoading: isUpdating }] = useUpdateClientAccountAccessMutation();
  const [updateClient] = useUpdateClientMutation();

  useEffect(() => {
    setLocalClient(client);
    setSelectedCampaigns({});
  }, [client]);

  // Build mapping from numeric IDs to ext IDs
  // Map: reporting account ID (number as string) -> ext_account_id
  const accountIdToExtIdMap = useMemo(() => {
    const map = new Map<string, string>();
    allAccounts.forEach(account => {
      // Try to match by account_id (numeric) or id (which might be numeric as string)
      if (account.account_id) {
        map.set(account.account_id.toString(), account.ext_account_id);
      }
      // Also map id to ext_account_id - id might be the numeric ID as a string
      map.set(account.id, account.ext_account_id);
      // Also try matching id as number if it's numeric
      const numericId = parseInt(account.id, 10);
      if (!isNaN(numericId)) {
        map.set(numericId.toString(), account.ext_account_id);
      }
    });
    return map;
  }, [allAccounts]);

  // Helper to find ext_campaign_id by matching account, platform, and campaign name
  // This is more reliable than trying to match numeric IDs
  const findExtCampaignId = (platform: string, accountId: string, campaignName: string): string | null => {
    const extAccountId = accountIdToExtIdMap.get(accountId);
    if (!extAccountId) return null;

    const account = allAccounts.find(a => a.ext_account_id === extAccountId && a.source === platform);
    if (!account) return null;

    const campaign = allCampaigns.find(c =>
      c.account.id === account.id &&
      c.title === campaignName &&
      c.platform === platform
    );

    return campaign?.ext_campaign_id || null;
  };

  // Initialize selected campaigns from client access data
  useEffect(() => {
    if (clientAccess.length > 0) {
      const initialSelected: Record<string, string[]> = {};
      clientAccess.forEach(platform => {
        platform.accounts.forEach(account => {
          const key = `${platform.source}_${account.account_id}`;
          initialSelected[key] = account.campaigns;
        });
      });
      setSelectedCampaigns(initialSelected);
    }
  }, [clientAccess]);

  // Set initial platform if none selected
  useEffect(() => {
    if (!isFetchingPlatforms && platforms.length > 0 && !currentPlatform) {
      setCurrentPlatform(platforms[0]);
    }
  }, [platforms, currentPlatform, isFetchingPlatforms]);

  // Set initial account if none selected
  useEffect(() => {
    if (!isFetchingAccounts && accounts.length > 0 && !currentAccount) {
      const sortedAccounts = [...accounts].sort((a, b) => customSort(a.name, b.name));
      setCurrentAccount(sortedAccounts[0].id.toString());
    }
  }, [accounts, currentAccount, isFetchingAccounts]);

  const createDefaultClientView = (accountId: string, accountName: string): ClientView => ({
    account_ext_id: accountId,
    account_name: accountName,
    platform: currentPlatform,
    show_difference: false,
    metrics: [],
    graph_one: {
      metric_one: null,
      metric_two: null,
    },
    graph_two: {
      metric_one: null,
      metric_two: null,
    },
    graph_three: {
      metric_one: null,
      metric_two: null,
    },
  });

  // Convert all selected campaigns to the new format and send all access
  const saveAllAccess = async() => {
    if (!localClient.ext_client_id) return;

    setSaveStatus('saving');
    try {
      // Group all selected campaigns by account and source
      const campaignsByAccountSource = new Map<string, string[]>();

      Object.entries(selectedCampaigns).forEach(([key, campaignIds]) => {
        const [platform, accountId] = key.split('_');
        const extAccountId = accountIdToExtIdMap.get(accountId);

        if (!extAccountId) {
          console.warn(`Could not find ext_account_id for account ${accountId}`);
          return;
        }

        const mapKey = `${extAccountId}:${platform}`;
        if (!campaignsByAccountSource.has(mapKey)) {
          campaignsByAccountSource.set(mapKey, []);
        }

        // Convert campaign IDs to ext_campaign_id
        // First, try to get campaign names from the current view if available
        const currentAccountCampaigns = currentAccount === accountId && currentPlatform === platform
          ? campaigns
          : [];

        // Also check clientAccess for campaign names if available
        const accessPlatform = clientAccess.find(p => p.source === platform);
        const accessAccount = accessPlatform?.accounts.find(a => a.account_id.toString() === accountId);

        const extCampaignIds: string[] = [];
        campaignIds.forEach(campaignId => {
          // Try to find the campaign name from current view
          const campaignName: string | undefined = currentAccountCampaigns.find(c => c.id.toString() === campaignId)?.name;

          // If not found, the campaign ID itself might be the name or we can't match it
          // Try to find in allCampaigns by matching account and trying the ID
          if (!campaignName) {
            // Try to find a campaign in allCampaigns that might match
            // This is a fallback - we'll try to match by account and see if any campaign ID matches
            const account = allAccounts.find(a =>
              (a.account_id?.toString() === accountId || a.id === accountId) &&
              a.source === platform
            );
            if (account) {
              // Try to find campaign - this is imperfect but best we can do
              const possibleCampaign = allCampaigns.find(c =>
                c.account.id === account.id &&
                c.platform === platform &&
                (c.id === campaignId || c.title.includes(campaignId))
              );
              if (possibleCampaign) {
                extCampaignIds.push(possibleCampaign.ext_campaign_id);
                return;
              }
            }
            console.warn(`Could not find campaign name for ID ${campaignId} in account ${accountId}, platform ${platform}`);
            return;
          }

          // Now find the ext_campaign_id using the campaign name
          const extCampaignId = findExtCampaignId(platform, accountId, campaignName);
          if (extCampaignId) {
            extCampaignIds.push(extCampaignId);
          } else {
            console.warn(`Could not find ext_campaign_id for campaign ${campaignName} in account ${accountId}, platform ${platform}`);
          }
        });

        campaignsByAccountSource.get(mapKey)!.push(...extCampaignIds);
      });

      // Create access requests for each account+source combination
      const accessRequests: ClientAccessRequest[] = Array.from(campaignsByAccountSource.entries()).map(([key, campaignIds]) => {
        const [accountId, source] = key.split(':');
        return {
          ext_client_id: localClient.ext_client_id,
          source: source,
          account: accountId,
          campaigns: campaignIds,
        };
      });

      // Send all access requests in one call
      await updateClientAccountAccess(accessRequests).unwrap();

      setSaveStatus('saved');
    } catch (error) {
      console.error('Failed to update campaign access:', error);
      setSaveStatus('idle');
      throw error;
    }
  };

  const handleUpdateAccess = async(campaignIds: string[]) => {
    if (!currentPlatform || !currentAccount) return;

    const key = `${currentPlatform}_${currentAccount}`;
    const currentSelected = selectedCampaigns[key] || [];

    setSelectedCampaigns(prev => ({
      ...prev,
      [key]: campaignIds,
    }));

    try {
      await saveAllAccess();

      // If this is the first time selecting campaigns for this account, add a new ClientView
      if (currentSelected.length === 0) {
        const currentAccountData = accounts.find(acc => acc.id.toString() === currentAccount);
        if (currentAccountData) {
          const extAccountId = accountIdToExtIdMap.get(currentAccount);
          const newView = createDefaultClientView(extAccountId || currentAccount, currentAccountData.name);
          const updatedViews = [...localClient.views, newView];
          const updatedClient = {
            ...localClient,
            views: updatedViews,
          };
          await updateClient({
            clientId: localClient.ext_client_id,
            data: updatedClient,
          });
          setLocalClient(updatedClient);
        }
      }
    } catch (error) {
      // Revert the selection if the update fails
      setSelectedCampaigns(prev => ({
        ...prev,
        [key]: currentSelected,
      }));
    }
  };

  const handleCampaignToggle = async(campaignId: string) => {
    if (!currentPlatform || !currentAccount) return;

    const key = `${currentPlatform}_${currentAccount}`;
    const currentSelected = selectedCampaigns[key] || [];
    const isDeselecting = currentSelected.includes(campaignId);
    const newSelected = isDeselecting
      ? currentSelected.filter(id => id !== campaignId)
      : [...currentSelected, campaignId];

    // Update local state
    setSelectedCampaigns(prev => ({
      ...prev,
      [key]: newSelected,
    }));

    try {
      // Save all access (the mutation deletes all and recreates)
      await saveAllAccess();

      // If this was the last campaign for this account, remove the corresponding ClientView
      if (isDeselecting && newSelected.length === 0) {
        const extAccountId = accountIdToExtIdMap.get(currentAccount);
        const updatedViews = localClient.views.filter(view =>
          view.account_ext_id !== currentAccount && view.account_ext_id !== extAccountId
        );
        const updatedClient = {
          ...localClient,
          views: updatedViews,
        };
        await updateClient({
          clientId: localClient.ext_client_id,
          data: updatedClient,
        });
        setLocalClient(updatedClient);
      }
    } catch (error) {
      // Revert the selection if the update fails
      setSelectedCampaigns(prev => ({
        ...prev,
        [key]: currentSelected,
      }));
    }
  };

  const handleSelectAll = async() => {
    if (currentPlatform && currentAccount) {
      const allCampaignIds = campaigns.map(campaign => campaign.id.toString());
      await handleUpdateAccess(allCampaignIds);
    }
  };

  const isCampaignSelected = (campaignId: string) => {
    const key = `${currentPlatform}_${currentAccount}`;
    return (selectedCampaigns[key] || []).includes(campaignId);
  };

  const hasSelectedCampaigns = (platform: string, accountId?: string) => {
    if (accountId) {
      const key = `${platform}_${accountId}`;
      return (selectedCampaigns[key] || []).length > 0;
    }
    const platformHasCampaigns = Object.entries(selectedCampaigns).some(([key, campaigns]) => {
      const platformKey = key.substring(0, key.lastIndexOf('_'));
      return platformKey === platform && campaigns.length > 0;
    });
    return platformHasCampaigns;
  };

  const dialogHeader = (
    <div className="flex items-center justify-between w-full">
      <span className="text-lg font-medium">
        Step 1: Assign Campaigns to Client
      </span>
      <div className="flex items-center gap-2">
        {(isUpdating || saveStatus === 'saving') && (
          <span className="text-base text-gray-500 h-6 leading-none mr-4">
            Saving...
          </span>
        )}
        {saveStatus === 'saved' && !isUpdating && (
          <span className="text-base text-gray-500 h-6 leading-none mr-4">
            Saved
          </span>
        )}
      </div>
    </div>
  );

  const filteredPlatforms = platforms
    .filter((platform) =>
      platform.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')
        .toLowerCase()
        .includes(platformSearch.toLowerCase())
    );

  const filteredAccounts = accounts
    .filter((account) =>
      account.name.toLowerCase().includes(accountSearch.toLowerCase())
    )
    .sort((a, b) => customSort(a.name, b.name));

  const filteredCampaigns = campaigns
    .filter((campaign) =>
      campaign.name.toLowerCase().includes(campaignSearch.toLowerCase())
    )
    .sort((a, b) => customSort(a.name, b.name));

  return (
    <Dialog
      visible={isOpen}
      onHide={onClose}
      header={dialogHeader}
      style={{ width: '90vw', maxWidth: '900px' }}
      contentStyle={{ paddingLeft: 8, paddingRight: 8 }}
      draggable={false}
      resizable={false}
      footer={(
        <div className="flex justify-end w-full">
          <Button
            onClick={onContinue}
            variant="primary"
            size="lg"
            className="w-full md:w-auto"
            disabled={
              isFetchingAccess ||
              isUpdating ||
              saveStatus === 'saving' ||
              isFetchingPlatforms ||
              isFetchingAccounts ||
              isFetchingCampaigns
            }
          >
            Next
          </Button>
        </div>
      )}
    >
      <div className="flex flex-col md:flex-row h-[70vh] min-h-[400px] w-full gap-2 md:gap-4">
        {/* Platforms Section */}
        <div className="flex-1 min-h-0 w-full md:w-1/3 min-w-0 border-b md:border-b-0 md:border-r border-gray-200 flex flex-col">
          <h3 className="font-medium px-4 pt-4">
            Platforms
          </h3>
          <SearchBar
            value={platformSearch}
            onChange={setPlatformSearch}
          />
          <div className="flex-1 overflow-y-auto px-4">
            {isFetchingPlatforms ? (
              <div className="space-y-2">
                {[1, 2, 3].map(i => (
                  <Skeleton
                    key={i}
                    className="h-8"
                  />
                ))}
              </div>
            ) : (
              <div className="space-y-1">
                {filteredPlatforms.map((platform, index) => (
                  <button
                    key={`${currentPlatform}_${platform}_${index}`}
                    onClick={() => {
                      setCurrentPlatform(platform);
                      setCurrentAccount('');
                    }}
                    className={`w-full text-left text-sm px-2 py-1 md:text-base md:px-3 md:py-1 rounded-lg flex items-center justify-between ${
                      currentPlatform === platform
                        ? 'bg-gray-100 font-medium'
                        : 'hover:bg-gray-50'
                    }`}
                  >
                    <span className="flex items-center gap-2">
                      {hasSelectedCampaigns(platform) && (
                        <span className="text-black text-xs">
                          ✔️
                        </span>
                      )}
                      {platform.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')}
                    </span>
                    {currentPlatform === platform && (
                      <span className="text-gray-500">
                        &gt;
                      </span>
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Accounts Section */}
        <div className="flex-1 min-h-0 w-full md:w-1/3 min-w-0 border-b md:border-b-0 md:border-r border-gray-200 flex flex-col">
          <h3 className="font-medium px-4 pt-4">
            Accounts
          </h3>
          <SearchBar
            value={accountSearch}
            onChange={setAccountSearch}
          />
          <div className="flex-1 overflow-y-auto px-4">
            {isFetchingAccounts ? (
              <div className="space-y-2">
                {[1, 2, 3].map(i => (
                  <Skeleton
                    key={i}
                    className="h-8"
                  />
                ))}
              </div>
            ) : (
              <div className="space-y-1">
                {filteredAccounts.map((account, index) => (
                  <button
                    key={`${currentPlatform}_${account.id}_${index}`}
                    onClick={() => setCurrentAccount(account.id.toString())}
                    className={`w-full text-left text-sm px-2 py-1 md:text-base md:px-3 md:py-1 rounded-lg flex items-center justify-between ${
                      currentAccount === account.id.toString()
                        ? 'bg-gray-100 font-medium'
                        : 'hover:bg-gray-50'
                    }`}
                  >
                    <span className="flex items-center gap-2">
                      {hasSelectedCampaigns(currentPlatform, account.id.toString()) && (
                        <span className="text-black text-xs">
                          ✔️
                        </span>
                      )}
                      {account.name}
                    </span>
                    {currentAccount === account.id.toString() && (
                      <span className="text-gray-500">
                        &gt;
                      </span>
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Campaigns Section */}
        <div className="flex-1 min-h-0 w-full md:w-1/3 min-w-0 flex flex-col">
          <div className="flex items-center justify-between px-4 pt-4">
            <h3 className="font-medium">
              Campaigns
            </h3>
            <button
              onClick={handleSelectAll}
              className="text-sm text-gray-500 hover:text-gray-700"
              type="button"
              disabled={isUpdating || !currentPlatform || !currentAccount}
            >
              Select all
            </button>
          </div>
          <SearchBar
            value={campaignSearch}
            onChange={setCampaignSearch}
          />
          <div className="flex-1 overflow-y-auto px-4">
            {isFetchingCampaigns ? (
              <div className="space-y-2">
                {[1, 2, 3].map(i => (
                  <Skeleton
                    key={i}
                    className="h-8"
                  />
                ))}
              </div>
            ) : (
              <div className="space-y-1">
                {filteredCampaigns.map((campaign, index) => (
                  <label
                    key={`${currentPlatform}_${currentAccount}_${campaign.id}_${index}`}
                    className="flex items-center space-x-3 text-sm px-2 py-1 md:text-base md:px-3 md:py-1 rounded-lg hover:bg-gray-50 cursor-pointer"
                  >
                    <input
                      type="checkbox"
                      checked={isCampaignSelected(campaign.id.toString())}
                      onChange={() => handleCampaignToggle(campaign.id.toString())}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      disabled={isUpdating}
                    />
                    <span>
                      {campaign.name}
                    </span>
                  </label>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </Dialog>
  );
};